import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config";

const initialFormData = {
  customer: { name: "", place: "", phone: "", notes: "" },
  driver: { name: "", license: "", phone: "", notes: "" },
  item: { name: "", sku: "", quantity: "", price: "", notes: "" },
};

const formConfig = {
  customer: {
    title: "Customer",
    fields: [
      {
        name: "name",
        label: "Customer Name",
        type: "text",
        placeholder: "Enter customer name",
      },
      {
        name: "place",
        label: "Place",
        type: "text",
        placeholder: "Enter place",
      },
      {
        name: "phone",
        label: "Phone",
        type: "text",
        placeholder: "Enter phone number",
      },
      {
        name: "notes",
        label: "Notes",
        type: "textarea",
        placeholder: "Optional notes",
      },
    ],
  },
  driver: {
    title: "Driver",
    fields: [
      {
        name: "name",
        label: "Driver Name",
        type: "text",
        placeholder: "Enter driver name",
      },
      {
        name: "license",
        label: "License No.",
        type: "text",
        placeholder: "Enter license number",
      },
      {
        name: "phone",
        label: "Phone",
        type: "text",
        placeholder: "Enter phone number",
      },
      {
        name: "notes",
        label: "Notes",
        type: "textarea",
        placeholder: "Optional notes",
      },
    ],
  },
  item: {
    title: "Item",
    fields: [
      {
        name: "name",
        label: "Item Name",
        type: "text",
        placeholder: "Enter item name",
      },
      {
        name: "sku",
        label: "SKU",
        type: "text",
        placeholder: "Enter SKU / code",
      },
      {
        name: "quantity",
        label: "Quantity",
        type: "number",
        placeholder: "0",
        min: "0",
      },
      {
        name: "price",
        label: "Price",
        type: "number",
        placeholder: "0.00",
        step: "0.01",
        min: "0",
      },
      {
        name: "notes",
        label: "Notes",
        type: "textarea",
        placeholder: "Optional notes",
      },
    ],
  },
};

export default function Lager() {
  const [activeTab, setActiveTab] = useState("customer");
  const [formData, setFormData] = useState(initialFormData);
  const [showModal, setShowModal] = useState(false);
  const [showExpenseTitleModal, setShowExpenseTitleModal] = useState(false);
  const [expenseTitle, setExpenseTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [expenseTitleError, setExpenseTitleError] = useState("");
  const [expenseTitleSuccessMessage, setExpenseTitleSuccessMessage] =
    useState("");
  const navigate = useNavigate();

  const currentConfig = formConfig[activeTab];
  const currentData = formData[activeTab];

  const openModal = (key) => {
    setActiveTab(key);
    setError("");
    setShowModal(true);
  };

  const openExpenseTitleModal = () => {
    setExpenseTitle("");
    setExpenseTitleError("");
    setExpenseTitleSuccessMessage("");
    setShowExpenseTitleModal(true);
  };

  const closeExpenseTitleModal = () => {
    setShowExpenseTitleModal(false);
    setExpenseTitleError("");
  };

  const handleExpenseTitleChange = (e) => {
    setExpenseTitle(e.target.value);
  };

  const saveExpenseTitle = (e) => {
    e.preventDefault();
    const title = expenseTitle?.trim();
    if (!title) {
      setExpenseTitleError("Enter an expense title");
      return;
    }

    const stored = JSON.parse(localStorage.getItem("expenseTitles") || "[]");
    const nextTitles = Array.from(new Set([...stored, title]));
    localStorage.setItem("expenseTitles", JSON.stringify(nextTitles));
    setExpenseTitleSuccessMessage("Expense title saved.");
    setExpenseTitle("");
    setShowExpenseTitleModal(false);
  };

  const closeModal = () => {
    setShowModal(false);
    setError("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [name]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      if (activeTab === "customer") {
        await axios.post(`${API_BASE_URL}/api/customers`, currentData);
        setSuccessMessage("Customer added successfully.");
        navigate("/customers");
      } else if (activeTab === "item") {
        await axios.post(`${API_BASE_URL}/api/stocks`, {
          ...currentData,
          quantity: Number(currentData.quantity || 0),
          price: Number(currentData.price || 0),
        });
        setSuccessMessage("Item added successfully.");
        navigate("/stocks");
      } else if (activeTab === "driver") {
        const stored = JSON.parse(localStorage.getItem("lagerDrivers") || "[]");
        const next = stored.filter((item) => item.name !== currentData.name);
        next.push(currentData);
        localStorage.setItem("lagerDrivers", JSON.stringify(next));
        setSuccessMessage("Driver added successfully.");
        navigate("/sales");
      }
    } catch (saveError) {
      console.error("Lager save failed", saveError);
      setError(
        saveError.response?.data?.message ||
          saveError.message ||
          "Failed to save. Check your data and try again.",
      );
    } finally {
      setSaving(false);
      setFormData((prev) => ({
        ...prev,
        [activeTab]: initialFormData[activeTab],
      }));
      setShowModal(false);
    }
  };

  return (
    <div className="container-fluid app-container">
      <div className="row mb-3">
        <div className="col-12 app-action-bar">
          <button
            type="button"
            className="btn btn-secondary app-btn me-2 mb-2"
            onClick={openExpenseTitleModal}
          >
            + Expense Title
          </button>
          
          {Object.keys(formConfig).map((key) => (
            <button
              key={key}
              type="button"
              className={`btn ${activeTab === key ? "btn-primary" : "btn-outline-primary"} app-btn me-2 mb-2`}
              onClick={() => openModal(key)}
            >
              Add {formConfig[key].title}
            </button>
          ))}
        </div>
      </div>

      {successMessage && (
        <div className="row mb-3">
          <div className="col-12">
            <div className="alert alert-success mb-0" role="status">
              {successMessage}
            </div>
          </div>
        </div>
      )}

      {showExpenseTitleModal && (
        <div
          className="app-modal-overlay"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeExpenseTitleModal();
          }}
        >
          <div
            className="app-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="expense-title-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="app-modal__header">
              <h4 className="app-modal__title" id="expense-title-modal-title">
                Add Expense Title
              </h4>
              <button
                type="button"
                className="app-modal__close"
                aria-label="Close"
                onClick={closeExpenseTitleModal}
              >
                ×
              </button>
            </div>
            <form onSubmit={saveExpenseTitle}>
              <div className="app-modal__body">
                <div className="mb-3">
                  <label className="form-label">Title</label>
                  <input
                    className="form-control"
                    type="text"
                    value={expenseTitle}
                    onChange={handleExpenseTitleChange}
                    placeholder="Enter new expense title"
                    required
                  />
                  {expenseTitleError && (
                    <div className="form-text text-danger">
                      {expenseTitleError}
                    </div>
                  )}
                </div>
              </div>
              <div className="app-modal__footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary me-2"
                  onClick={closeExpenseTitleModal}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save Title
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showModal && (
        <div
          className="app-modal-overlay"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div
            className="app-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="lager-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="app-modal__header">
              <h4 className="app-modal__title" id="lager-modal-title">
                Add {currentConfig.title}
              </h4>
              <button
                type="button"
                className="app-modal__close"
                aria-label="Close"
                onClick={closeModal}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="app-modal__body">
                {currentConfig.fields.map((field) => (
                  <div className="mb-3" key={field.name}>
                    <label className="form-label">{field.label}</label>
                    {field.type === "textarea" ? (
                      <textarea
                        className="form-control"
                        name={field.name}
                        value={currentData[field.name]}
                        onChange={handleChange}
                        rows={3}
                        placeholder={field.placeholder}
                      />
                    ) : (
                      <input
                        className="form-control"
                        type={field.type}
                        name={field.name}
                        value={currentData[field.name]}
                        onChange={handleChange}
                        placeholder={field.placeholder}
                        step={field.step}
                        min={field.min}
                      />
                    )}
                  </div>
                ))}
                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}
              </div>
              <div className="app-modal__footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving
                    ? `Saving ${currentConfig.title}…`
                    : `Save ${currentConfig.title}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
